import { api } from './client';
import { useAuthStore } from '@/stores/authStore';
import type { Tab, TabSyncRequest, TabSyncResponse, ContentAnalysis, ApiBrowserTab } from '@/types';

interface CreateTabRequest {
  url: string;
  title?: string;
  favicon?: string;
  tab_id?: string;
}

interface UpdateTabRequest {
  url?: string;
  title?: string;
  favicon?: string;
}

interface AnalyzeTabResponse {
  id: string;
  tab_id: string;
  content_type: string;
  summary: string;
  entities: string[];
  topics: string[];
  sentiment: string;
  language: string;
}

// Tab Group types
export interface TabGroup {
  id: string;
  name: string;
  color: string;
  tabCount: number;
  tabs: Array<{ id: string; title: string; url: string; favicon?: string }>;
  createdAt: string;
  isAIGenerated: boolean;
}

// AI-suggested grouping returned by GET /tabs-intelligence/recommendations/:session_id
// for entries with `type === 'group'`. These suggestions are not yet persisted —
// the UI calls `createTabGroup(name, tabIds)` to materialize them.
export interface AISuggestedGroup {
  type: 'group';
  groupName: string;
  color?: string;
  tabIds: string[];
  confidence: number;
  reasoning: string;
  count: number;
}

// Tab Security types
export type SecurityFindingSeverity = 'high' | 'medium' | 'low';

/** One URL-heuristic finding from the backend security scan. */
export interface SecurityFinding {
  type: string;
  severity: SecurityFindingSeverity;
  description: string;
  method?: string;
}

export interface TabSecurity {
  id: string;
  tabId: string;
  url?: string;
  score: number;
  /** Human-readable finding descriptions (kept for older consumers). */
  threats: string[];
  findings: SecurityFinding[];
  recommendations: string[];
  status: 'safe' | 'warning' | 'danger';
  lastScanned: string;
  encryption: boolean;
}

export interface SecuritySummary {
  totalTabsScanned: number;
  threatsDetected: number;
  high: number;
  medium: number;
  low: number;
  securityScore: number;
  lastScannedAt: string;
}

// Tab Prediction types
export interface TabPrediction {
  id: string;
  tab: string;
  url: string;
  confidence: number;
  reason: string;
  timing: string;
  category: string;
}

export interface PredictionStats {
  accuracy_percent: number;
  time_saved_minutes: number;
  accepted_count: number;
  feedback_count: number;
}

export function formatPredictionTimeSaved(minutes: number): string {
  if (minutes <= 0) return '0m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder > 0 ? `${hours}h ${remainder}m` : `${hours}h`;
}

export function estimatePotentialTimeSavedMinutes(predictions: TabPrediction[]): number {
  return Math.round(predictions.reduce((sum, p) => sum + (p.confidence / 100) * 2, 0));
}

function categoryFromPredictionUrl(url: string): string {
  try {
    const h = new URL(url).hostname.replace(/^www\./, '');
    return h || 'General';
  } catch {
    return 'General';
  }
}

function relativeTimeLabel(millis: number): string {
  const seconds = Math.floor(millis / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

function mapAgentTabPredictionToUi(p: Record<string, unknown>, idx: number): TabPrediction {
  const tabId = p.tab_id != null ? String(p.tab_id) : '';
  const url = String(p.url ?? '');
  const id = tabId || `pred-${idx}-${url.slice(0, 48)}`;
  const title = String(p.title ?? '');
  let conf = Number(p.confidence ?? 0);
  if (!Number.isFinite(conf)) conf = 0;
  if (conf > 0 && conf <= 1) conf = Math.round(conf * 100);
  else conf = Math.round(Math.min(100, Math.max(0, conf)));
  const rawPredictedAt = p.predicted_at != null ? String(p.predicted_at) : '';
  let timing = 'Suggested next';
  if (rawPredictedAt) {
    const ts = new Date(rawPredictedAt).getTime();
    if (!Number.isNaN(ts)) {
      timing = relativeTimeLabel(Date.now() - ts);
    }
  }
  return {
    id,
    tab: title || (url ? url : 'Suggested tab'),
    url,
    confidence: conf,
    reason: String(p.reason ?? ''),
    timing,
    category: categoryFromPredictionUrl(url),
  };
}

// Tab Notification types
export interface TabNotification {
  id: string;
  type: 'info' | 'warning' | 'success' | 'error';
  title: string;
  message: string;
  /** Optional human-readable tab label (sourced from server metadata.tab). */
  tab?: string;
  /** Optional related tab id (sourced from server metadata.tab_id). */
  tabId?: string;
  /** ISO-8601 timestamp from the server (`created_at`). */
  createdAt: string;
  read: boolean;
}

function mapNotificationFromApi(raw: Record<string, unknown>): TabNotification {
  const typeRaw = String(raw.type ?? 'info').toLowerCase();
  const type: TabNotification['type'] =
    typeRaw === 'warning' || typeRaw === 'success' || typeRaw === 'error' ? typeRaw : 'info';
  const tabId = raw.tab_id != null ? String(raw.tab_id) : undefined;
  const tab = raw.tab != null ? String(raw.tab) : undefined;
  return {
    id: String(raw.id ?? ''),
    type,
    title: String(raw.title ?? ''),
    message: String(raw.message ?? ''),
    tab: tab || undefined,
    tabId: tabId || undefined,
    createdAt: String(raw.created_at ?? raw.createdAt ?? ''),
    read: Boolean(raw.read),
  };
}

// Smart Action types
export interface SmartAction {
  id: string;
  name: string;
  description: string;
  trigger: string;
  actions: string[];
  status: 'active' | 'paused';
  runs: number;
  lastRun?: string;
}

// Workflow types
export interface Workflow {
  id: string;
  name: string;
  description: string;
  trigger: string;
  actions: string[];
  status: 'active' | 'paused' | 'archived';
  runs: number;
  lastRun?: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  trigger: string;
  actions: string[];
}

export interface WorkflowHistoryEntry {
  id: string;
  workflow_id: string;
  status: string;
  details: string;
  executed_at: string;
}

export interface BrowserAutomationScript {
  id: string;
  name: string;
  description: string;
  trigger: string;
  actions: string[];
  is_active: boolean;
  run_count: number;
  last_run_at?: string;
}

export interface WebhookSubscription {
  id: string;
  name: string;
  url: string;
  events: string[];
  status: 'active' | 'inactive';
  success_rate: number;
  last_triggered?: string;
}

// Tab Version types
export interface TabVersion {
  id: string;
  tabId: string;
  versionNumber: number;
  snapshotType: 'manual' | 'scheduled' | 'triggered' | 'auto';
  url: string;
  title: string;
  favicon?: string;
  isFavorite: boolean;
  createdAt: string;
  updatedAt?: string;
  notes?: string;
}

// Workspace-level version timeline entry
export interface TabVersionEntry {
  version_id: string;
  version_num: number;
  tab_id: string;
  tab_title: string;
  tab_url: string;
  change_type: string;
  description: string;
  created_at: string;
  snapshot?: Record<string, unknown>;
}

// Search types
export interface SearchResult {
  id: string;
  title: string;
  url: string;
  snippet: string;
  relevance: number;
  category: string;
  timestamp: string;
  tags?: string[];
  favicon?: string;
  starred?: boolean;
  matchedOn?: string[];
}

/** Optional filters for GET /tab-semantic-search (advanced search). */
export interface TabSemanticSearchFilters {
  q: string;
  limit?: number;
  tags?: string[];
  date_range?: string;
  starred_only?: boolean;
}

/** Saved visual layout from GET/POST /visual-organization/layouts */
export interface SavedVisualLayout {
  id: string;
  name: string;
  layout: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface TimelineEntry {
  id: string;
  time: string;
  action: 'tab_created' | 'tab_visited' | 'tab_updated' | 'tab_closed' | 'tab_focused' | 'tab_unfocused';
  tabId: string;
  tabTitle: string;
  tabUrl: string;
  duration?: number;
  metadata?: {
    scrollDepth?: number;
    interactionCount?: number;
  };
}

export interface TabHistory {
  tabId: string;
  tabTitle: string;
  tabUrl: string;
  history: {
    timestamp: string;
    action: string;
    duration?: number;
  }[];
}

function tabGroupFromApiPayload(raw: Record<string, unknown>): TabGroup {
  const tabsRaw = raw.tabs;
  const tabs: TabGroup['tabs'] = Array.isArray(tabsRaw)
    ? (tabsRaw as TabGroup['tabs'])
    : [];
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    color: String(raw.color ?? '#96CEB4'),
    tabCount: Number(raw.tabCount ?? 0),
    tabs,
    createdAt: String(raw.createdAt ?? ''),
    isAIGenerated: Boolean(raw.isAIGenerated),
  };
}

function mapTimelineEntry(raw: Record<string, unknown>, index: number): TimelineEntry {
  const tabId = String(raw.tab_id ?? raw.tabId ?? '');
  const title = String(raw.tab_title ?? raw.title ?? raw.tabTitle ?? '');
  const url = String(raw.tab_url ?? raw.url ?? raw.tabUrl ?? '');
  const metadata = raw.metadata && typeof raw.metadata === 'object'
    ? (raw.metadata as Record<string, unknown>)
    : undefined;

  return {
    id: String(raw.id ?? `${tabId || 'tab'}-${index}`),
    time: String(raw.time ?? raw.timestamp ?? new Date().toISOString()),
    action: String(raw.action ?? 'tab_visited') as TimelineEntry['action'],
    tabId,
    tabTitle: title,
    tabUrl: url,
    duration: typeof raw.duration === 'number' ? raw.duration : undefined,
    metadata: metadata
      ? {
          scrollDepth: typeof metadata.scrollDepth === 'number' ? metadata.scrollDepth : undefined,
          interactionCount:
            typeof metadata.interactionCount === 'number' ? metadata.interactionCount : undefined,
        }
      : undefined,
  };
}

function mapTabHistory(raw: Record<string, unknown>): TabHistory {
  const historyRaw = Array.isArray(raw.history) ? raw.history : [];
  return {
    tabId: String(raw.tab_id ?? raw.tabId ?? ''),
    tabTitle: String(raw.tab_title ?? raw.title ?? raw.tabTitle ?? ''),
    tabUrl: String(raw.tab_url ?? raw.url ?? raw.tabUrl ?? ''),
    history: historyRaw.map((entry) => {
      const row = (entry ?? {}) as Record<string, unknown>;
      return {
        timestamp: String(row.timestamp ?? row.time ?? ''),
        action: String(row.action ?? ''),
        duration: typeof row.duration === 'number' ? row.duration : undefined,
      };
    }),
  };
}

// Collaboration types
export interface Collaborator {
  id: string;
  name: string;
  avatar: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  viewing?: string;
}

export interface SharedSession {
  id: string;
  name: string;
  owner: string;
  collaborators: string[];
  tabCount: number;
  activity: string;
  createdAt: string;
}

export interface CollaborationActivityRow {
  user: string;
  action: string;
  target: string;
  resource_id?: string;
  time: string;
  /** Pre-formatted relative time, e.g. "2m ago". Server may provide. */
  timeRel?: string;
}

export type WorkspaceRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface WorkspaceMember {
  user_id: number;
  user_uuid: string;
  display_name: string;
  email: string;
  role: WorkspaceRole | string;
  joined_at: string;
  status: string;
}

export interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  workspace_name?: string;
  email: string;
  role: WorkspaceRole | string;
  invited_by: number;
  created_at: string;
  expires_at: string;
}

export interface WorkspaceRoleInfo {
  workspace_id: string;
  user_id: string;
  role: WorkspaceRole | string;
  is_owner: boolean;
}

export interface WorkspaceTab {
  id: string;
  url?: string;
  title?: string;
  favicon?: string;
}

function mapCollaborator(raw: Record<string, unknown>): Collaborator {
  const statusRaw = String(raw.status ?? 'offline').toLowerCase();
  const status: Collaborator['status'] =
    statusRaw === 'online' || statusRaw === 'away' || statusRaw === 'busy'
      ? (statusRaw as Collaborator['status'])
      : 'offline';
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? 'Member'),
    avatar: String(raw.avatar ?? '?'),
    status,
    viewing: raw.viewing != null ? String(raw.viewing) : undefined,
  };
}

function mapSharedSession(raw: Record<string, unknown>): SharedSession {
  const collab = Array.isArray(raw.collaborators)
    ? (raw.collaborators as unknown[]).map((c) => String(c))
    : [];
  const tabCount =
    typeof raw.tabCount === 'number'
      ? raw.tabCount
      : typeof raw.tab_count === 'number'
        ? raw.tab_count
        : 0;
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? 'Workspace'),
    owner: String(raw.owner ?? ''),
    collaborators: collab,
    tabCount,
    activity: String(raw.activity ?? ''),
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
  };
}

// Proactive Intelligence types
export interface Insight {
  id: string;
  type: 'suggestion' | 'warning' | 'opportunity' | 'pattern';
  title: string;
  description: string;
  confidence: number;
  timestamp: string;
  actions?: Array<{ label: string; primary?: boolean }>;
  dismissed?: boolean;
}

export interface Pattern {
  id: string;
  name: string;
  frequency: string;
  lastOccurred: string;
  trend: 'increasing' | 'stable' | 'decreasing';
  confidence: number;
}

export interface ProactiveSettings {
  proactiveSuggestions: boolean;
  patternLearning: boolean;
  distractionAlerts: boolean;
  autoActions: boolean;
  updatedAt?: string;
}

// Agent types
export interface Agent {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'idle' | 'error';
  tasksCompleted: number;
  /** Average response time in milliseconds (0 = unknown / not recorded). */
  avgResponseTime: number;
  successRate: number;
  currentTask?: string;
  skillCount?: number;
  capabilityCount?: number;
  hasRuns?: boolean;
  /** Where this pool row came from — drives execute behavior in the orchestration UI. */
  source?: 'roster' | 'suggestion' | 'smart_action' | 'placeholder';
}

export interface Task {
  id: string;
  description: string;
  assignedAgent: string;
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
  priority: 'low' | 'medium' | 'high';
  startedAt?: string;
  completedAt?: string;
  duration?: number;
}

// Distraction Blocking types
export interface BlockRule {
  id: string;
  name: string;
  sites: string[];
  schedule: string;
  isActive: boolean;
  blockedToday: number;
}

function mapBlockRule(raw: Record<string, unknown>, index: number): BlockRule {
  const sites = Array.isArray(raw.sites)
    ? raw.sites.map((v) => String(v))
    : raw.url
      ? [String(raw.url)]
      : raw.domain
        ? [String(raw.domain)]
        : [];
  const scheduleRaw = raw.schedule;
  let schedule = 'Always';
  if (typeof scheduleRaw === 'string') {
    schedule = scheduleRaw;
  } else if (scheduleRaw && typeof scheduleRaw === 'object') {
    schedule = 'Scheduled';
  }
  return {
    id: String(raw.id ?? `rule-${index}`),
    name: String(raw.name ?? raw.category ?? `Rule ${index + 1}`),
    sites,
    schedule,
    isActive: Boolean(raw.isActive ?? raw.is_active ?? true),
    blockedToday: Number(raw.blockedToday ?? raw.blocked_today ?? raw.today_blocks ?? raw.hit_count ?? 0),
  };
}

function mapThreatStatus(score: number): TabSecurity['status'] {
  if (score < 40) return 'danger';
  if (score < 70) return 'warning';
  return 'safe';
}

// Must match urlRiskPenalty in internal/services/url_risk.go.
const SECURITY_SEVERITY_PENALTY: Record<SecurityFindingSeverity, number> = { high: 40, medium: 20, low: 5 };

function toSeverity(raw: unknown): SecurityFindingSeverity {
  return raw === 'high' || raw === 'medium' ? raw : 'low';
}

function isFindingRecord(item: unknown): item is Record<string, unknown> {
  return typeof item === 'object' && item !== null && 'severity' in item;
}

function mapFindings(raw: unknown): SecurityFinding[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isFindingRecord).map((f) => ({
    type: String(f.type ?? 'unknown'),
    severity: toSeverity(f.severity),
    description: String(f.description ?? f.type ?? ''),
    method: f.method ? String(f.method) : undefined,
  }));
}

function scoreFromFindings(findings: SecurityFinding[]): number {
  const penalty = findings.reduce((sum, f) => sum + SECURITY_SEVERITY_PENALTY[f.severity], 0);
  return Math.max(0, 100 - penalty);
}

function statusFor(score: number, findings: SecurityFinding[]): TabSecurity['status'] {
  if (findings.some((f) => f.severity === 'high')) return 'danger';
  if (findings.some((f) => f.severity === 'medium')) return 'warning';
  return mapThreatStatus(score);
}

/**
 * Maps a security scan payload (POST /security/tabs/:id/scan) or a legacy
 * threat record. Never invents a score: when the payload carries none, it is
 * derived from the findings using the backend's severity weights.
 */
function mapThreatToTabSecurity(tabId: string, threat: Record<string, unknown>): TabSecurity {
  const findings = mapFindings(threat.findings);
  const legacyThreats = Array.isArray(threat.threats)
    ? threat.threats.map((t) => String(t))
    : Array.isArray(threat.threat_indicators)
      ? threat.threat_indicators.map((t) => String(t))
      : [];
  const rawScore = Number(threat.security_score ?? threat.trust_score ?? threat.score);
  const score = Number.isFinite(rawScore)
    ? Math.max(0, Math.min(100, Math.round(rawScore)))
    : scoreFromFindings(findings);
  const url = typeof threat.url === 'string' ? threat.url : undefined;
  const encryption = url !== undefined
    ? /^https:\/\//i.test(url)
    : typeof threat.encryption === 'boolean'
      ? threat.encryption
      : !findings.some((f) => f.type === 'insecure_connection' || f.type === 'insecure_credentials_page');
  return {
    id: String(threat.id ?? `security-${tabId}`),
    tabId: String(threat.tab_id ?? tabId),
    url,
    score,
    threats: findings.length > 0 ? findings.map((f) => f.description) : legacyThreats,
    findings,
    recommendations: Array.isArray(threat.recommendations) ? threat.recommendations.map((r) => String(r)) : [],
    status: findings.length > 0 ? statusFor(score, findings) : mapThreatStatus(score),
    lastScanned: String(threat.scanned_at ?? threat.last_scanned ?? new Date().toISOString()),
    encryption,
  };
}

// Focus Mode types
export interface FocusModeStatus {
  enabled: boolean;
  startTime?: string;
  duration?: number;
  sessionId?: string;
}

export interface FocusSession {
  id: string;
  startTime: string;
  endTime?: string;
  duration: number;
  goal: string;
  completed: boolean;
  distractions: number;
}

export interface FocusStats {
  totalFocusTime: number;
  sessionsCompleted: number;
  averageDistractions: number;
  longestStreak: number;
  productivityScore: number;
}

function mapFocusModeStatus(raw: Record<string, unknown>): FocusModeStatus {
  const duration = Number(raw.planned_duration_minutes ?? raw.duration ?? 25);
  return {
    enabled: Boolean(raw.enabled),
    startTime: raw.start_time != null ? String(raw.start_time) : undefined,
    duration: Number.isFinite(duration) ? duration : 25,
    sessionId: raw.session_id != null ? String(raw.session_id) : undefined,
  };
}

function mapFocusSession(raw: Record<string, unknown>): FocusSession {
  const startRaw = raw.start_time ?? raw.startTime;
  const endRaw = raw.end_time ?? raw.endTime;
  let duration = Number(raw.duration ?? 0);
  if ((!duration || !Number.isFinite(duration)) && startRaw && endRaw) {
    const start = new Date(String(startRaw)).getTime();
    const end = new Date(String(endRaw)).getTime();
    if (!Number.isNaN(start) && !Number.isNaN(end) && end > start) {
      duration = Math.max(1, Math.round((end - start) / 60000));
    }
  }
  if (!Number.isFinite(duration) || duration <= 0) {
    duration = Number(raw.planned_duration_minutes ?? 25);
  }
  const distractions = Number(raw.distractions ?? 0);
  const goal = String(raw.goal ?? raw.notes ?? 'Focus session');
  const completed = raw.completed != null
    ? Boolean(raw.completed)
    : Boolean(endRaw && raw.is_active === false);
  return {
    id: String(raw.id ?? ''),
    startTime: startRaw != null ? String(startRaw) : new Date().toISOString(),
    endTime: endRaw != null ? String(endRaw) : undefined,
    duration: Number.isFinite(duration) ? duration : 0,
    goal,
    completed,
    distractions: Number.isFinite(distractions) ? distractions : 0,
  };
}

function mapFocusStats(raw: Record<string, unknown>): FocusStats {
  const totalFocusTime = Number(raw.total_focus_minutes ?? raw.totalFocusTime ?? 0);
  const sessionsCompleted = Number(raw.completed_sessions ?? raw.sessionsCompleted ?? 0);
  const longestStreak = Number(raw.streak_days ?? raw.longestStreak ?? 0);
  const averageDistractions = Number(raw.average_distractions ?? raw.averageDistractions ?? 0);
  let productivityScore = Number(raw.productivity_score ?? raw.productivityScore ?? 0);
  if (!productivityScore && totalFocusTime > 0) {
    productivityScore = Math.min(100, Math.round((totalFocusTime / 240) * 100));
  }
  return {
    totalFocusTime: Number.isFinite(totalFocusTime) ? totalFocusTime : 0,
    sessionsCompleted: Number.isFinite(sessionsCompleted) ? sessionsCompleted : 0,
    averageDistractions: Number.isFinite(averageDistractions) ? averageDistractions : 0,
    longestStreak: Number.isFinite(longestStreak) ? longestStreak : 0,
    productivityScore: Number.isFinite(productivityScore) ? productivityScore : 0,
  };
}

// Pomodoro types
export interface PomodoroStatus {
  active: boolean;
  type: 'work' | 'short_break' | 'long_break';
  timeRemaining: number;
  sessionsCompleted: number;
  sessionId?: string;
  isPaused?: boolean;
}

export interface PomodoroSession {
  id: string;
  type: 'work' | 'short_break' | 'long_break';
  duration: number;
  startTime: string;
  endTime?: string;
  completed: boolean;
}

export interface PomodoroStats {
  todayPomodoros: number;
  totalFocusMinutes: number;
  currentStreak: number;
}

function mapPomodoroType(raw: unknown): PomodoroStatus['type'] {
  const t = String(raw ?? 'work');
  if (t === 'short_break' || t === 'long_break') return t;
  if (t === 'break') return 'short_break';
  return 'work';
}

function mapPomodoroStatus(raw: Record<string, unknown>): PomodoroStatus {
  const plannedSeconds = Number(raw.planned_duration ?? 0);
  return {
    active: Boolean(raw.active),
    type: mapPomodoroType(raw.type),
    timeRemaining: Number.isFinite(plannedSeconds) ? plannedSeconds : 25 * 60,
    sessionsCompleted: Number(raw.sessions_completed ?? raw.sessionsCompleted ?? 0),
    sessionId: raw.session_id != null ? String(raw.session_id) : undefined,
    isPaused: Boolean(raw.is_paused ?? raw.isPaused),
  };
}

function mapPomodoroSession(raw: Record<string, unknown>): PomodoroSession {
  const durationSeconds = Number(raw.duration ?? raw.planned_duration ?? 0);
  const durationMinutes = durationSeconds > 120 ? Math.round(durationSeconds / 60) : Math.max(1, Math.round(durationSeconds / 60) || Number(raw.duration_minutes ?? 25));
  return {
    id: String(raw.id ?? ''),
    type: mapPomodoroType(raw.type ?? raw.session_type),
    duration: durationMinutes,
    startTime: String(raw.start_time ?? raw.startTime ?? new Date().toISOString()),
    endTime: raw.end_time != null ? String(raw.end_time) : undefined,
    completed: Boolean(raw.is_completed ?? raw.completed ?? false),
  };
}

function mapPomodoroStats(raw: Record<string, unknown>): PomodoroStats {
  return {
    todayPomodoros: Number(raw.completed_today ?? raw.todayPomodoros ?? 0),
    totalFocusMinutes: Number(raw.total_work_minutes ?? raw.totalFocusMinutes ?? 0),
    currentStreak: Number(raw.streak ?? raw.currentStreak ?? 0),
  };
}

function mapHabitFromApi(raw: Record<string, unknown>): Habit {
  const colors = ['blue', 'purple', 'green', 'orange'];
  const category = String(raw.category ?? raw.habit_type ?? 'Productivity');
  const colorIdx = Math.abs(category.split('').reduce((s, c) => s + c.charCodeAt(0), 0)) % colors.length;
  const weekProgress = Array.isArray(raw.week_progress)
    ? raw.week_progress.map((v) => Boolean(v))
    : Array.isArray(raw.weekProgress)
      ? raw.weekProgress.map((v) => Boolean(v))
      : [false, false, false, false, false, false, false];
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? raw.title ?? 'Habit'),
    goal: String(raw.goal ?? raw.description ?? ''),
    streak: Number(raw.streak ?? raw.current_streak ?? 0),
    completedToday: Boolean(raw.completed_today ?? raw.completedToday ?? false),
    weekProgress,
    category,
    color: String(raw.color ?? colors[colorIdx]),
  };
}

// Habit/Goal types
export interface Habit {
  id: string;
  name: string;
  goal: string;
  streak: number;
  completedToday: boolean;
  weekProgress: boolean[];
  category: string;
  color: string;
}

export interface HabitAchievement {
  id: string;
  name: string;
  description: string;
  unlocked: boolean;
  icon: string;
}

class TabService {
  // Tab CRUD
  async getTabsBySession(sessionId: string, page = 1, limit = 50): Promise<{ tabs: ApiBrowserTab[]; total: number }> {
    try {
      const response = await api.get<{ tabs: ApiBrowserTab[]; total: number }>(`/sessions/${sessionId}/tabs`, {
        params: { page, limit },
      });
      return { tabs: response.data.tabs, total: response.data.total };
    } catch (err: any) {
      // No session exists yet for this user — treat as empty rather
      // than erroring in the console. The page can render an empty
      // state and the user can start populating tabs.
      if (err?.response?.status === 404) {
        return { tabs: [], total: 0 };
      }
      throw err;
    }
  }

  async createTab(sessionId: string, request: CreateTabRequest): Promise<ApiBrowserTab> {
    const body = {
      ...request,
      session_id: sessionId,
      tab_id: (request as { tab_id?: string }).tab_id ?? crypto.randomUUID(),
    };
    const response = await api.post<ApiBrowserTab>(`/sessions/${sessionId}/tabs`, body);
    return response.data;
  }

  async syncTabs(sessionId: string, request: TabSyncRequest): Promise<TabSyncResponse> {
    const response = await api.post<TabSyncResponse>(`/sessions/${sessionId}/tabs/sync`, request);
    return response.data;
  }

  async getTabChanges(sessionId: string, since?: string): Promise<unknown[]> {
    const response = await api.get<{ changes: unknown[] }>(`/sessions/${sessionId}/tabs/changes`, {
      params: since ? { since } : undefined,
    });
    return response.data.changes;
  }

  async getTabCount(sessionId: string): Promise<{ count: number; active: number; closed: number }> {
    const response = await api.get<{ count: number; active: number; closed: number }>(
      `/sessions/${sessionId}/tabs/count`
    );
    return response.data;
  }

  async getSuggestedAppTypes(sessionId: string): Promise<unknown[]> {
    const response = await api.get<{ suggestions: unknown[] }>(`/sessions/${sessionId}/tabs/suggestions`);
    return response.data.suggestions;
  }

  async generateAppFromTabs(
    sessionId: string,
    request: { tab_ids: string[]; app_type: string; app_name?: string; description?: string; tech_stack?: string }
  ): Promise<{ id: string } & Record<string, unknown>> {
    const response = await api.post(`/sessions/${sessionId}/tabs/generate`, request, {
      timeout: 300_000,
    });
    return response.data as { id: string } & Record<string, unknown>;
  }

  /**
   * Generates an app via SSE and resolves when generation_completed fires.
   * Avoids the default 30s axios timeout on long LLM runs.
   */
  generateAppFromTabsStreamAndWait(
    sessionId: string,
    request: { tab_ids: string[]; app_type: string; app_name?: string; description?: string },
    options?: { onProgress?: (percent: number, message: string) => void; timeoutMs?: number },
  ): Promise<{ app_id: string; app_name?: string; app_type?: string }> {
    return new Promise((resolve, reject) => {
      const es = this.generateAppFromTabsStream(sessionId, request);
      const timeoutMs = options?.timeoutMs ?? 300_000;
      let settled = false;

      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        es.close();
        fn();
      };

      const timer = setTimeout(() => {
        finish(() => reject(new Error('App generation timed out after 5 minutes. Try fewer tabs or retry.')));
      }, timeoutMs);

      es.addEventListener('generation_progress', (e: MessageEvent) => {
        try {
          const data = JSON.parse(String(e.data)) as { percent?: number; message?: string };
          options?.onProgress?.(data.percent ?? 0, data.message ?? 'Generating…');
        } catch {
          /* ignore malformed progress */
        }
      });

      es.addEventListener('generation_completed', (e: MessageEvent) => {
        try {
          const data = JSON.parse(String(e.data)) as { app_id?: string; app_name?: string; app_type?: string };
          finish(() =>
            resolve({
              app_id: String(data.app_id ?? ''),
              app_name: typeof data.app_name === 'string' ? data.app_name : request.app_name,
              app_type: typeof data.app_type === 'string' ? data.app_type : request.app_type,
            }),
          );
        } catch {
          finish(() => reject(new Error('Invalid generation response from server')));
        }
      });

      es.addEventListener('generation_failed', (e: MessageEvent) => {
        try {
          const data = JSON.parse(String(e.data)) as { error?: string };
          finish(() => reject(new Error(data.error ?? 'Generation failed')));
        } catch {
          finish(() => reject(new Error('Generation failed')));
        }
      });

      es.onerror = () => {
        finish(() => reject(new Error('Lost connection to generation stream')));
      };
    });
  }

  /**
   * Generates an app from tabs using SSE streaming for real-time progress updates.
   * Returns an EventSource that emits progress events.
   */
  generateAppFromTabsStream(
    sessionId: string,
    request: { tab_ids: string[]; app_type: string; app_name?: string; description?: string },
  ): EventSource {
    const params = new URLSearchParams({
      app_type: request.app_type,
      tab_ids: request.tab_ids.join(','),
    });
    if (request.app_name) params.set('app_name', request.app_name);
    if (request.description) params.set('description', request.description);

    let token = '';
    try {
      const tokenVal = useAuthStore.getState().tokens?.token;
      token = tokenVal || '';
    } catch {
      token = localStorage.getItem('authToken') || '';
    }
    if (token) {
      params.set('token', token);
    }

    const url = `/api/v1/sessions/${sessionId}/apps/generate/stream?${params.toString()}`;
    return new EventSource(url);
  }

  async getTab(tabId: string): Promise<Tab> {
    const response = await api.get<Tab>(`/tabs/${tabId}`);
    return response.data;
  }

  async updateTab(tabId: string, request: UpdateTabRequest): Promise<Tab> {
    const response = await api.put<Tab>(`/tabs/${tabId}`, request);
    return response.data;
  }

  async deleteTab(tabId: string): Promise<void> {
    await api.delete(`/tabs/${tabId}`);
  }

  async closeTab(tabId: string): Promise<Tab> {
    const response = await api.post<Tab>(`/tabs/${tabId}/close`);
    return response.data;
  }

  async analyzeTab(tabId: string): Promise<AnalyzeTabResponse> {
    const response = await api.post<AnalyzeTabResponse>(`/tabs/${tabId}/analyze`);
    return response.data;
  }

  async getTabAnalyses(tabId: string, limit = 10): Promise<ContentAnalysis[]> {
    const response = await api.get<{ analyses: ContentAnalysis[] }>(`/tabs/${tabId}/analyses`, {
      params: { limit },
    });
    return response.data.analyses;
  }

  // Tab Intelligence / Groups
  async getTabGroups(sessionId: string): Promise<{ groups: TabGroup[] }> {
    const response = await api.get<{ groups: TabGroup[] }>(`/tabs-intelligence/groups/${sessionId}`);
    return response.data;
  }

  async createTabGroup(name: string, tabIds: string[], sessionId?: string): Promise<TabGroup> {
    console.log("createTabGroup called with:", { name, tabIds, sessionId });
    const body: Record<string, unknown> = { name, tab_ids: tabIds };
    if (sessionId) {
      body.session_id = sessionId;
    }
    console.log("Sending request to /tabs-intelligence/groups with body:", body);
    try {
      const response = await api.post<{ success?: boolean; data?: Record<string, unknown> }>('/tabs-intelligence/groups', body);
      console.log("Response from createTabGroup:", response);
      const raw = response.data?.data;
      if (!response.data?.success || !raw || typeof raw !== 'object') {
        throw new Error('Invalid tab group response');
      }
      
      const tabGroup = tabGroupFromApiPayload(raw as Record<string, unknown>);
      
      // Tell the Chrome extension to physically group the tabs in the browser
      const rawTabs = Array.isArray(raw.tabs) ? raw.tabs : [];
      const urls = rawTabs
        .map((t: any) => t.url)
        .filter(Boolean);
        
      if (urls.length > 0) {
        console.log("Sending groupTabs command to extension for urls:", urls);
        window.postMessage({
          source: 'khiimori-app',
          action: 'groupTabs',
          payload: {
            name: tabGroup.name,
            color: tabGroup.color,
            urls: urls
          }
        }, '*');
      }
      
      return tabGroup;
    } catch (err) {
      console.error("Error in createTabGroup:", err);
      throw err;
    }
  }

  async updateTabGroup(
    groupId: string,
    data: Partial<TabGroup> & { tab_ids?: string[] },
  ): Promise<TabGroup> {
    const body: { name?: string; color?: string; tab_ids?: string[] } = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.color !== undefined) body.color = data.color;
    if (data.tab_ids !== undefined) body.tab_ids = data.tab_ids;

    const response = await api.put<{ success?: boolean; data?: Record<string, unknown> }>(
      `/tabs-intelligence/groups/${groupId}`,
      body,
    );
    const resBody = response.data;
    const raw = resBody?.data;
    if (!resBody?.success || !raw || typeof raw !== 'object') {
      throw new Error('Invalid tab group response');
    }
    return tabGroupFromApiPayload(raw as Record<string, unknown>);
  }

  async deleteTabGroup(groupId: string): Promise<void> {
    await api.delete(`/tabs-intelligence/groups/${groupId}`);
  }

  async getTabRecommendations(sessionId: string): Promise<unknown[]> {
    const response = await api.get<{ recommendations: unknown[] }>(`/tabs-intelligence/recommendations/${sessionId}`);
    return response.data.recommendations;
  }

  async getGroupSuggestions(sessionId: string): Promise<AISuggestedGroup[]> {
    const response = await api.get<{ recommendations: Record<string, unknown>[] }>(
      `/tabs-intelligence/recommendations/${sessionId}`,
    );
    const raw = Array.isArray(response.data?.recommendations) ? response.data.recommendations : [];
    return raw
      .filter((r) => (r as { type?: string }).type === 'group')
      .map((r) => {
        const tabIdsRaw = (r as { tab_ids?: unknown }).tab_ids;
        const tabIds = Array.isArray(tabIdsRaw) ? tabIdsRaw.map((id) => String(id)).filter(Boolean) : [];
        const groupName = String(
          (r as { group_name?: unknown; title?: unknown }).group_name ??
            (r as { title?: unknown }).title ??
            'Suggested group',
        );
        const colorRaw = (r as { color?: unknown }).color;
        const reasoningRaw = (r as { reasoning?: unknown }).reasoning;
        const confidenceRaw = (r as { confidence?: unknown }).confidence;
        const countRaw = (r as { count?: unknown }).count;
        const confidence = Number(confidenceRaw ?? 0);
        return {
          type: 'group' as const,
          groupName,
          color: typeof colorRaw === 'string' && colorRaw ? colorRaw : undefined,
          tabIds,
          confidence: Number.isFinite(confidence) ? confidence : 0,
          reasoning: typeof reasoningRaw === 'string' ? reasoningRaw : '',
          count: Number(countRaw ?? tabIds.length) || tabIds.length,
        };
      });
  }

  async getTabTimeline(
    sessionId: string,
    range?: 'today' | 'week' | 'month'
  ): Promise<TimelineEntry[]> {
    const response = await api.get<{ entries?: Record<string, unknown>[]; timeline?: Record<string, unknown>[] }>(
      `/tab-timeline/session/${sessionId}`,
      { params: range ? { range } : undefined }
    );
    const rows = response.data.entries ?? response.data.timeline ?? [];
    return rows.map((row, index) => mapTimelineEntry(row, index));
  }

  async getTabHistory(tabId: string): Promise<TabHistory> {
    const response = await api.get<Record<string, unknown>>(`/tab-timeline/tab/${tabId}`);
    return mapTabHistory(response.data ?? {});
  }

  // Tab Security
  async getTabSecurity(tabId: string): Promise<TabSecurity> {
    const response = await api.get<{ threats?: Record<string, unknown>[] }>(`/security/tabs/${tabId}/threats`);
    const items = response.data.threats ?? [];
    // Current backend: a list of findings. Older shape: a single threat record.
    if (items.length > 0 && items.every(isFindingRecord)) {
      return mapThreatToTabSecurity(tabId, { findings: items });
    }
    return mapThreatToTabSecurity(tabId, items[0] ?? {});
  }

  async scanTabSecurity(tabId: string): Promise<TabSecurity> {
    const response = await api.post<Record<string, unknown>>(`/security/tabs/${tabId}/scan`, { tab_id: tabId });
    return mapThreatToTabSecurity(tabId, response.data ?? {});
  }

  async getSecuritySummary(sessionId?: string): Promise<SecuritySummary> {
    const response = await api.get<Record<string, unknown>>('/security/summary', {
      params: sessionId ? { session_id: sessionId } : undefined,
    });
    const d = response.data ?? {};
    const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    return {
      totalTabsScanned: num(d.total_tabs_scanned),
      threatsDetected: num(d.threats_detected),
      high: num(d.high_severity),
      medium: num(d.medium_severity),
      low: num(d.low_severity),
      securityScore: num(d.security_score),
      lastScannedAt: String(d.last_scanned_at ?? new Date().toISOString()),
    };
  }

  /** Session-level scan summary (kept for callers that only need the aggregate). */
  async scanAllTabSecurity(sessionId?: string): Promise<{ scans: TabSecurity[] }> {
    const summary = await this.getSecuritySummary(sessionId);
    if (summary.totalTabsScanned <= 0) {
      return { scans: [] };
    }
    const findings: SecurityFinding[] = [];
    return {
      scans: [
        {
          id: 'security-summary',
          tabId: 'summary',
          score: summary.securityScore,
          threats: [],
          findings,
          recommendations: [],
          status: summary.high > 0 ? 'danger' : summary.medium > 0 ? 'warning' : mapThreatStatus(summary.securityScore),
          lastScanned: summary.lastScannedAt,
          encryption: true,
        },
      ],
    };
  }


  // Tab Predictions (POST /agent/predict-tabs → map to UI shape)
  async getPredictions(context?: string): Promise<{ predictions: TabPrediction[]; status?: string }> {
    const response = await api.post<{ predictions: Record<string, unknown>[]; status?: string }>(
      '/agent/predict-tabs',
      { context: context ?? 'current_context' }
    );
    const raw = response.data.predictions ?? [];
    return {
      predictions: raw.map((p, idx) => mapAgentTabPredictionToUi(p, idx)),
      status: response.data.status,
    };
  }

  /** Records user acceptance of a prediction (telemetry); pair with window.open in the UI. */
  async applyPrediction(predictionId: string, accepted = true): Promise<void> {
    await api.post('/smart-tab-prediction/feedback', {
      prediction_id: predictionId,
      accepted,
    });
  }

  async getPredictionStats(): Promise<PredictionStats> {
    const response = await api.get<Record<string, unknown>>('/smart-tab-prediction/stats');
    const data = response.data ?? {};
    return {
      accuracy_percent: Number(data.accuracy_percent ?? 0),
      time_saved_minutes: Number(data.time_saved_minutes ?? 0),
      accepted_count: Number(data.accepted_count ?? 0),
      feedback_count: Number(data.feedback_count ?? 0),
    };
  }

  // Tab Notifications
  async getNotifications(): Promise<TabNotification[]> {
    const response = await api.get<{ notifications: Record<string, unknown>[] }>('/notifications');
    const rows = response.data.notifications ?? [];
    return rows.map((r) => mapNotificationFromApi(r));
  }

  async markNotificationRead(notificationId: string): Promise<void> {
    await api.put(`/notifications/${notificationId}/read`);
  }

  async markAllNotificationsRead(): Promise<void> {
    await api.put('/notifications/read-all');
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await api.delete(`/notifications/${notificationId}`);
  }

  // Tab Search (unified bookmarks + tabs; see TabSemanticSearchService)
  async semanticSearch(query: string): Promise<SearchResult[]> {
    return this.semanticSearchWithFilters({ q: query, limit: 50 });
  }

  /** Unified tab + bookmark search with optional advanced filters. */
  async semanticSearchWithFilters(filters: TabSemanticSearchFilters): Promise<SearchResult[]> {
    const { q, limit = 50, tags, date_range, starred_only } = filters;
    const params: Record<string, string | number | boolean> = { q, limit };
    if (tags && tags.length > 0) {
      params.tags = tags.join(',');
    }
    if (date_range && date_range !== 'all-time') {
      params.date_range = date_range;
    }
    if (starred_only) {
      params.starred_only = true;
    }
    const response = await api.get<{ results: Record<string, unknown>[] }>('/tab-semantic-search', {
      params,
    });
    const rows = response.data.results ?? [];
    return rows.map((r) => TabService.mapSearchResultRow(r));
  }

  private static mapSearchResultRow(r: Record<string, unknown>): SearchResult {
    const rel = r.relevance;
    const relevance =
      typeof rel === 'number' ? rel : rel !== undefined ? Number(rel) || 0 : 0;
    const tagsRaw = r.tags;
    const tags = Array.isArray(tagsRaw)
      ? tagsRaw.map((t) => String(t)).filter((t) => t.length > 0)
      : undefined;
    const matchedRaw = r.matched_on ?? r.matchedOn;
    const matchedOn = Array.isArray(matchedRaw)
      ? matchedRaw.map((m) => String(m)).filter((m) => m.length > 0)
      : undefined;
    const fav = r.favicon != null ? String(r.favicon).trim() : '';
    return {
      id: String(r.id ?? ''),
      title: String(r.title ?? ''),
      url: String(r.url ?? ''),
      snippet: String(r.snippet ?? ''),
      relevance,
      category: String(r.category ?? ''),
      timestamp: String(r.timestamp ?? ''),
      tags: tags && tags.length > 0 ? tags : undefined,
      favicon: fav || undefined,
      starred: typeof r.starred === 'boolean' ? r.starred : undefined,
      matchedOn: matchedOn && matchedOn.length > 0 ? matchedOn : undefined,
    };
  }

  /** Distinct bookmark tags for filter chips (GET /bookmarks/tags). */
  async listBookmarkTags(): Promise<string[]> {
    const response = await api.get<{ tags: string[] }>('/bookmarks/tags');
    return response.data.tags ?? [];
  }

  // Visual Tab Organization — layouts (in-memory on server; see VisualTabOrganizationService)
  async listVisualLayouts(): Promise<SavedVisualLayout[]> {
    const response = await api.get<{ layouts: SavedVisualLayout[] }>('/visual-organization/layouts');
    return response.data.layouts ?? [];
  }

  async saveVisualLayout(name: string, layout: Record<string, unknown>): Promise<SavedVisualLayout> {
    const response = await api.post<SavedVisualLayout>('/visual-organization/layouts', { name, layout });
    return response.data;
  }

  async updateVisualLayout(id: string, name: string, layout: Record<string, unknown>): Promise<void> {
    await api.put(`/visual-organization/layouts/${id}`, { name, layout });
  }

  async deleteVisualLayout(id: string): Promise<void> {
    await api.delete(`/visual-organization/layouts/${id}`);
  }

  // Tab Version History
  async getTabVersions(tabId: string): Promise<TabVersion[]> {
    const response = await api.get<{ versions: TabVersion[] }>(`/version-history/tab/${tabId}`);
    return response.data.versions;
  }

  async restoreTabVersion(tabId: string, versionId: string): Promise<{ message?: string; browser_update_queued?: boolean }> {
    const response = await api.post(`/version-history/tab/${tabId}/restore/${versionId}`);
    return response.data;
  }

  async getVersionDetails(versionId: string): Promise<TabVersion> {
    const response = await api.get<TabVersion>(`/version-history/${versionId}`);
    return response.data;
  }

  // Workspace-level version endpoints
  async getWorkspaceVersionTimeline(workspaceId: string): Promise<TabVersionEntry[]> {
    const response = await api.get<{ versions: TabVersionEntry[]; total: number }>(`/version-history/workspace/${workspaceId}/versions`);
    return response.data.versions;
  }

  async createWorkspaceCheckpoint(workspaceId: string, description: string): Promise<{ checkpoint_id: string; tabs_snapshotted: number }> {
    const response = await api.post<{ checkpoint_id: string; tabs_snapshotted: number; description: string }>(`/version-history/workspace/${workspaceId}/checkpoint`, { description });
    return response.data;
  }

  async getVersionStorageStats(workspaceId: string): Promise<{ total_versions: number; auto_saves: number; manual_checkpoints: number; storage: string; storage_kb: number; tab_count: number }> {
    const response = await api.get<{ total_versions: number; auto_saves: number; manual_checkpoints: number; storage: string; storage_kb: number; tab_count: number }>(`/version-history/workspace/${workspaceId}/stats`);
    return response.data;
  }

  // Smart Actions / Workflows
  async getSmartActions(): Promise<SmartAction[]> {
    const response = await api.get<{ actions?: SmartAction[]; suggestions?: SmartAction[] }>('/smart-actions/suggestions');
    return response.data.actions ?? response.data.suggestions ?? [];
  }

  async executeSmartAction(actionId: string): Promise<void> {
    await api.post('/smart-actions/execute', { action_id: actionId });
  }

  async getSmartActionHistory(): Promise<unknown[]> {
    const response = await api.get<{ history: unknown[] }>('/smart-actions/history');
    return response.data.history;
  }

  async recordSmartActionExecution(payload: {
    action_id?: string;
    suggestion_id?: string;
    title?: string;
    type?: string;
    workflow_id?: string;
  }): Promise<void> {
    await api.post('/smart-actions/record', payload);
  }

  async updateSmartActionRule(ruleId: string, data: Partial<SmartAction>): Promise<void> {
    await api.put(`/smart-actions/rules/${ruleId}`, data);
  }

  async deleteSmartActionRule(ruleId: string): Promise<void> {
    await api.delete(`/smart-actions/rules/${ruleId}`);
  }

  async getWorkflows(): Promise<Workflow[]> {
    const response = await api.get<{ workflows: Workflow[] }>('/workflows');
    return response.data.workflows ?? [];
  }

  async createWorkflow(payload: {
    name: string;
    description?: string;
    trigger?: string;
    actions?: string[];
    steps?: Array<{ action: string; target?: string }>;
  }): Promise<string> {
    const response = await api.post<{ workflow_id: string }>('/workflows', payload);
    return response.data.workflow_id;
  }

  async updateWorkflow(id: string, payload: Partial<Workflow> & { actions?: string[] }): Promise<Workflow> {
    const response = await api.put<{ success: boolean; workflow: Workflow }>(`/workflows/${id}`, payload);
    return response.data.workflow;
  }

  async deleteWorkflow(id: string): Promise<void> {
    await api.delete(`/workflows/${id}`);
  }

  async executeWorkflow(id: string): Promise<void> {
    await api.post(`/workflows/${id}/execute`);
  }

  async updateWorkflowStatus(id: string, status: 'active' | 'paused' | 'archived'): Promise<Workflow> {
    const response = await api.post<{ success: boolean; workflow: Workflow }>(`/workflows/${id}/status`, { status });
    return response.data.workflow;
  }

  async getWorkflowTemplates(): Promise<WorkflowTemplate[]> {
    const response = await api.get<{ templates: WorkflowTemplate[] }>('/workflows/templates');
    return response.data.templates ?? [];
  }

  async applyWorkflowTemplate(templateId: string): Promise<string> {
    const response = await api.post<{ workflow_id: string }>(`/workflows/templates/${templateId}/apply`);
    return response.data.workflow_id;
  }

  async getWorkflowHistory(workflowId: string): Promise<WorkflowHistoryEntry[]> {
    const response = await api.get<{ history: WorkflowHistoryEntry[] }>(`/workflows/${workflowId}/history`);
    return response.data.history ?? [];
  }

  async getBrowserAutomationScripts(): Promise<BrowserAutomationScript[]> {
    const response = await api.get<{ scripts: BrowserAutomationScript[] }>('/browser-automation/scripts');
    return response.data.scripts ?? [];
  }

  async createBrowserAutomationScript(payload: {
    name: string;
    description?: string;
    trigger?: string;
    actions?: string[];
  }): Promise<string> {
    const response = await api.post<{ script_id: string }>('/browser-automation/scripts', payload);
    return response.data.script_id;
  }

  async runBrowserAutomationScript(scriptId: string): Promise<void> {
    await api.post(`/browser-automation/scripts/${scriptId}/run`);
  }

  async updateBrowserAutomationScriptStatus(scriptId: string, isActive: boolean): Promise<void> {
    await api.put(`/browser-automation/scripts/${scriptId}/status`, { is_active: isActive });
  }

  async deleteBrowserAutomationScript(scriptId: string): Promise<void> {
    await api.delete(`/browser-automation/scripts/${scriptId}`);
  }

  async getBrowserAutomationStats(): Promise<{ total_runs: number; time_saved: string; tabs_affected: number }> {
    const response = await api.get<{ total_runs: number; time_saved: string; tabs_affected: number }>('/browser-automation/stats');
    return response.data;
  }

  async getWebhooks(): Promise<WebhookSubscription[]> {
    const response = await api.get<{ webhooks: WebhookSubscription[] }>('/webhooks');
    return response.data.webhooks ?? [];
  }

  async createWebhook(payload: { name: string; url: string; events: string[] }): Promise<string> {
    const response = await api.post<{ id: string }>('/webhooks', payload);
    return response.data.id;
  }

  async updateWebhook(id: string, payload: Partial<WebhookSubscription>): Promise<void> {
    await api.put(`/webhooks/${id}`, payload);
  }

  async deleteWebhook(id: string): Promise<void> {
    await api.delete(`/webhooks/${id}`);
  }

  async testWebhook(id: string): Promise<void> {
    await api.post(`/webhooks/${id}/test`);
  }

  async getWebhookDeliveries(id: string): Promise<Array<{ id: string; status: string; created_at: string; statusCode?: number }>> {
    const response = await api.get<{ deliveries: Array<{ id: string; status: string; created_at: string; statusCode?: number }> }>(`/webhooks/${id}/deliveries`);
    return response.data.deliveries ?? [];
  }

  // Collaboration
  async getSharedWorkspaces(): Promise<SharedSession[]> {
    const response = await api.get<{ workspaces?: Record<string, unknown>[] }>('/shared-workspaces');
    const raw = response.data.workspaces ?? [];
    return raw.map((w) => mapSharedSession(w as Record<string, unknown>));
  }

  async createWorkspace(name: string): Promise<SharedSession> {
    const response = await api.post<Record<string, unknown>>('/shared-workspaces', { name });
    return mapSharedSession(response.data);
  }

  async getSharedWorkspaceDetail(workspaceId: string): Promise<{
    workspace: SharedSession;
    members: Record<string, unknown>[];
    tab_ids: string[];
    role?: string;
    is_owner?: boolean;
  }> {
    const response = await api.get<{
      workspace?: Record<string, unknown>;
      members?: Record<string, unknown>[];
      tab_ids?: string[];
      role?: string;
      is_owner?: boolean;
    }>(`/shared-workspaces/${workspaceId}`);
    const w = response.data.workspace ?? {};
    return {
      workspace: mapSharedSession(w),
      members: response.data.members ?? [],
      tab_ids: response.data.tab_ids ?? [],
      role: response.data.role,
      is_owner: response.data.is_owner,
    };
  }

  async updateSharedWorkspace(
    workspaceId: string,
    data: { name?: string; description?: string; is_public?: boolean }
  ): Promise<SharedSession> {
    const response = await api.put<Record<string, unknown>>(`/shared-workspaces/${workspaceId}`, data);
    return mapSharedSession(response.data);
  }

  async addWorkspaceMember(workspaceId: string, userId: string): Promise<void> {
    await api.post(`/shared-workspaces/${workspaceId}/members`, { user_id: userId });
  }

  async removeWorkspaceMember(workspaceId: string, userId: string): Promise<void> {
    await api.delete(`/shared-workspaces/${workspaceId}/members/${userId}`);
  }

  async deleteSharedWorkspace(workspaceId: string): Promise<void> {
    await api.delete(`/shared-workspaces/${workspaceId}`);
  }

  async getCollaborators(): Promise<Collaborator[]> {
    const response = await api.get<{ users?: Record<string, unknown>[] }>('/shared-workspaces/collaborators');
    const raw = response.data.users ?? [];
    return raw.map((u) => mapCollaborator(u));
  }

  async getCollaborationHubActivity(): Promise<CollaborationActivityRow[]> {
    const response = await api.get<{ activities?: Record<string, unknown>[] }>(
      '/shared-workspaces/activity-summary'
    );
    const raw = response.data.activities ?? [];
    return raw.map((a) => ({
      user: String(a.user ?? ''),
      action: String(a.action ?? ''),
      target: String(a.target ?? ''),
      resource_id: a.resource_id != null ? String(a.resource_id) : undefined,
      time: String(a.timeRel ?? a.time ?? ''),
    }));
  }

  /** Returns the caller's role inside a workspace (and whether they're the owner). */
  async getWorkspaceRole(workspaceId: string): Promise<WorkspaceRoleInfo> {
    const response = await api.get<WorkspaceRoleInfo>(`/shared-workspaces/${workspaceId}/role`);
    return response.data;
  }

  /** Returns the pending invitations for a workspace (admin only). */
  async getWorkspaceInvitations(workspaceId: string): Promise<WorkspaceInvitation[]> {
    const response = await api.get<{ invitations: WorkspaceInvitation[] }>(
      `/shared-workspaces/${workspaceId}/invitations`
    );
    return response.data.invitations ?? [];
  }

  /** Returns pending invitations addressed to the signed-in user's email. */
  async getMyWorkspaceInvitations(): Promise<WorkspaceInvitation[]> {
    const response = await api.get<{ invitations: WorkspaceInvitation[] }>('/shared-workspaces/my-invitations');
    return response.data.invitations ?? [];
  }

  /** Creates an invitation by email + role (admin only). */
  async inviteToWorkspace(
    workspaceId: string,
    email: string,
    role: WorkspaceRole
  ): Promise<WorkspaceInvitation> {
    const response = await api.post<{ invitation: WorkspaceInvitation }>(
      `/shared-workspaces/${workspaceId}/invite`,
      { email, role }
    );
    return response.data.invitation;
  }

  /** Cancels a pending invitation (admin only). */
  async cancelWorkspaceInvitation(workspaceId: string, invitationId: string): Promise<void> {
    await api.delete(`/shared-workspaces/${workspaceId}/invitations/${invitationId}`);
  }

  /** Accepts an invitation via its ID token. */
  async acceptWorkspaceInvitation(invitationToken: string): Promise<{ workspace_id: string; role: string }> {
    const response = await api.post<{ workspace_id: string; role: string }>(
      `/shared-workspaces/invitations/${invitationToken}/accept`
    );
    return response.data;
  }

  /** Updates a member's role (admin only). */
  async updateWorkspaceMemberRole(
    workspaceId: string,
    userId: string,
    role: WorkspaceRole
  ): Promise<void> {
    await api.patch(`/shared-workspaces/${workspaceId}/members/${userId}`, { role });
  }

  /** Transfers ownership to a new user UUID (owner only). */
  async transferWorkspaceOwnership(workspaceId: string, newOwnerUuid: string): Promise<SharedSession> {
    const response = await api.post<Record<string, unknown>>(
      `/shared-workspaces/${workspaceId}/transfer-ownership`,
      { new_owner_uuid: newOwnerUuid }
    );
    return mapSharedSession(response.data);
  }

  /** Unassigns a tab from a workspace (editor+). */
  async unassignWorkspaceTab(workspaceId: string, tabId: string): Promise<void> {
    await api.delete(`/shared-workspaces/${workspaceId}/tabs/${tabId}`);
  }

  /** Assigns a single tab to a workspace (editor+). */
  async assignTabToWorkspace(workspaceId: string, tabId: string): Promise<void> {
    await api.post(`/shared-workspaces/${workspaceId}/tabs/${tabId}/assign`);
  }

  /** Returns enriched tab list (id, title, url, favicon) for a workspace. */
  async getWorkspaceTabsExpanded(workspaceId: string): Promise<WorkspaceTab[]> {
    const response = await api.get<{ tabs: WorkspaceTab[] }>(
      `/shared-workspaces/${workspaceId}/tabs/expanded`
    );
    return response.data.tabs ?? [];
  }

  /** Leaves a shared workspace (non-owners only). */
  async leaveWorkspace(workspaceId: string): Promise<void> {
    await api.post(`/shared-workspaces/${workspaceId}/leave`);
  }

  /** Sends a presence heartbeat (server throttles broadcasts). */
  async heartbeatWorkspace(workspaceId: string): Promise<void> {
    await api.post(`/shared-workspaces/${workspaceId}/heartbeat`);
  }

  // Focus Mode
  async getFocusModeStatus(): Promise<FocusModeStatus> {
    const response = await api.get<Record<string, unknown>>('/focus-mode/status');
    return mapFocusModeStatus(response.data);
  }

  async enableFocusMode(durationSeconds?: number, sessionGoal?: string): Promise<void> {
    const durationMinutes = durationSeconds ? Math.max(1, Math.round(durationSeconds / 60)) : undefined;
    await api.post('/focus-mode/enable', {
      duration_minutes: durationMinutes,
      session_goal: sessionGoal,
    });
  }

  async disableFocusMode(completed = false): Promise<void> {
    await api.post('/focus-mode/disable', { completed });
  }

  async getFocusSessions(): Promise<FocusSession[]> {
    const response = await api.get<{ sessions: Record<string, unknown>[] }>('/focus-mode/sessions');
    return (response.data.sessions ?? []).map(mapFocusSession);
  }

  async getFocusStats(): Promise<FocusStats> {
    const response = await api.get<Record<string, unknown>>('/focus-mode/stats');
    return mapFocusStats(response.data);
  }

  // Pomodoro
  async startPomodoro(
    sessionType: 'work' | 'short_break' | 'long_break' = 'work',
    durationMinutes?: number
  ): Promise<string | undefined> {
    const response = await api.post<Record<string, unknown>>('/pomodoro/start', {
      session_type: sessionType,
      type: sessionType,
      ...(durationMinutes != null && durationMinutes > 0 ? { duration_minutes: durationMinutes } : {}),
    });
    return response.data.id != null ? String(response.data.id) : undefined;
  }

  async pausePomodoro(): Promise<void> {
    await api.post('/pomodoro/pause');
  }

  async resumePomodoro(): Promise<void> {
    await api.post('/pomodoro/resume');
  }

  async stopPomodoro(): Promise<void> {
    await api.post('/pomodoro/stop');
  }

  async startBreak(breakType: 'short_break' | 'long_break' = 'short_break'): Promise<void> {
    await api.post('/pomodoro/break', { break_type: breakType });
  }

  async getPomodoroStatus(): Promise<PomodoroStatus> {
    const response = await api.get<Record<string, unknown>>('/pomodoro/status');
    return mapPomodoroStatus(response.data);
  }

  async getPomodoroHistory(): Promise<PomodoroSession[]> {
    const response = await api.get<{ sessions?: Record<string, unknown>[]; history?: Record<string, unknown>[] }>(
      '/pomodoro/history'
    );
    const rows = response.data.sessions ?? response.data.history ?? [];
    return rows.map(mapPomodoroSession);
  }

  async getPomodoroStats(): Promise<PomodoroStats> {
    const response = await api.get<Record<string, unknown>>('/pomodoro/stats');
    return mapPomodoroStats(response.data);
  }

  // Distraction Blocking
  async getBlockRules(): Promise<BlockRule[]> {
    const response = await api.get<{ rules: Record<string, unknown>[] }>('/distraction-blocking/rules');
    const rows = response.data.rules ?? [];
    return rows.map((row, index) => mapBlockRule(row, index));
  }

  async createBlockRule(data: Omit<BlockRule, 'id' | 'blockedToday'>): Promise<BlockRule> {
    const response = await api.post<Record<string, unknown>>('/distraction-blocking/rules', {
      name: data.name,
      sites: data.sites,
      pattern: data.sites[0],
    });
    const raw = response.data;
    return mapBlockRule(
      {
        ...raw,
        sites: raw.sites ?? (raw.domain ? [raw.domain] : data.sites),
        name: raw.name ?? data.name,
        is_active: true,
      },
      0,
    );
  }

  async updateBlockRule(ruleId: string, data: Partial<BlockRule>): Promise<void> {
    await api.put(`/distraction-blocking/rules/${ruleId}`, {
      name: data.name,
      is_active: data.isActive,
    });
  }

  async deleteBlockRule(ruleId: string): Promise<void> {
    await api.delete(`/distraction-blocking/rules/${ruleId}`);
  }

  async toggleBlockRule(ruleId: string): Promise<void> {
    await api.put('/distraction-blocking/toggle', { rule_id: ruleId });
  }

  async getBlockedStats(): Promise<{ blockedToday: number; timeSaved: string }> {
    const rules = await this.getBlockRules();
    const blockedToday = rules.reduce((sum, rule) => sum + (rule.blockedToday || 0), 0);
    const timeSaved = `${Math.round(blockedToday * 3)}m`;
    return { blockedToday, timeSaved };
  }

  // Habits
  async getHabits(): Promise<Habit[]> {
    const response = await api.get<{ habits: Record<string, unknown>[] }>('/habits');
    return (response.data.habits ?? []).map(mapHabitFromApi);
  }

  async createHabit(data: Omit<Habit, 'id' | 'streak' | 'completedToday' | 'weekProgress'>): Promise<Habit> {
    const response = await api.post<Record<string, unknown>>('/habits', {
      name: data.name,
      description: data.goal,
      category: data.category,
      color: data.color,
    });
    return mapHabitFromApi(response.data.habit ?? response.data);
  }

  async completeHabit(habitId: string): Promise<Habit> {
    const response = await api.post<Record<string, unknown>>(`/habits/${habitId}/complete`);
    return mapHabitFromApi((response.data.habit ?? response.data) as Record<string, unknown>);
  }

  async uncompleteHabit(habitId: string): Promise<Habit> {
    const response = await api.post<Record<string, unknown>>(`/habits/${habitId}/uncomplete`);
    return mapHabitFromApi((response.data.habit ?? response.data) as Record<string, unknown>);
  }

  async graceDayHabit(habitId: string): Promise<Habit> {
    const response = await api.post<Record<string, unknown>>(`/habits/${habitId}/grace-day`);
    return mapHabitFromApi((response.data.habit ?? response.data) as Record<string, unknown>);
  }

  async deleteHabit(habitId: string): Promise<void> {
    await api.delete(`/habits/${habitId}`);
  }

  async getHabitStats(): Promise<{
    totalStreak: number;
    completedToday: number;
    achievements: HabitAchievement[];
  }> {
    const response = await api.get<{ stats: Record<string, unknown> }>('/habits/stats');
    const stats = response.data.stats ?? {};
    const achievements = Array.isArray(stats.achievements)
      ? stats.achievements.map((a) => {
          const row = a as Record<string, unknown>;
          return {
            id: String(row.id ?? ''),
            name: String(row.name ?? ''),
            description: String(row.description ?? ''),
            unlocked: Boolean(row.unlocked),
            icon: String(row.icon ?? 'trophy'),
          };
        })
      : [];
    return {
      totalStreak: Number(stats.total_streak ?? 0),
      completedToday: Number(stats.completed_today ?? 0),
      achievements,
    };
  }

  // Goals / Habits (legacy goals endpoint)
  async getGoals(): Promise<Habit[]> {
    const response = await api.get<{ goals: Record<string, unknown>[] }>('/goals');
    return (response.data.goals ?? []).map((g) =>
      mapHabitFromApi({
        ...g,
        name: g.title ?? g.name,
        goal: g.description ?? g.goal,
      })
    );
  }

  async createGoal(data: Omit<Habit, 'id' | 'streak' | 'completedToday' | 'weekProgress'>): Promise<Habit> {
    const response = await api.post<Habit>('/goals', data);
    return response.data;
  }

  async updateGoal(goalId: string, data: Partial<Habit>): Promise<Habit> {
    const response = await api.put<Habit>(`/goals/${goalId}`, data);
    return response.data;
  }

  async deleteGoal(goalId: string): Promise<void> {
    await api.delete(`/goals/${goalId}`);
  }

  async updateGoalProgress(goalId: string, completed: boolean): Promise<void> {
    await api.post(`/goals/${goalId}/progress`, { completed });
  }

  // Proactive Intelligence
  async getProactiveSuggestions(): Promise<Insight[]> {
    const response = await api.get<{ suggestions: Insight[] }>('/proactive-intelligence/suggestions');
    return response.data.suggestions;
  }

  async analyzeUserBehavior(lookbackDays = 30): Promise<void> {
    await api.post('/proactive-intelligence/analyze', { lookback_days: lookbackDays });
  }

  async getDetectedPatterns(): Promise<Pattern[]> {
    const response = await api.get<{ patterns: Pattern[] }>('/proactive-intelligence/patterns');
    return response.data.patterns;
  }

  async provideSuggestionFeedback(suggestionId: string, feedback: 'accepted' | 'rejected'): Promise<void> {
    await api.post(`/proactive-intelligence/suggestions/${suggestionId}/feedback`, { feedback });
  }

  async getProactiveInsightSummary(): Promise<{
    activeSuggestions: number;
    patternCount: number;
    lastAnalysisAt?: string;
  }> {
    const response = await api.get<{
      insights: {
        active_suggestions: number;
        pattern_count: number;
        last_analysis_at?: string;
      };
    }>('/proactive-intelligence/insights');
    const row = response.data.insights;
    return {
      activeSuggestions: row.active_suggestions,
      patternCount: row.pattern_count,
      lastAnalysisAt: row.last_analysis_at,
    };
  }

  async getProactiveSettings(): Promise<ProactiveSettings> {
    const response = await api.get<{
      settings: {
        proactive_suggestions: boolean;
        pattern_learning: boolean;
        distraction_alerts: boolean;
        auto_actions: boolean;
        updated_at?: string;
      };
    }>('/proactive-intelligence/settings');
    const settings = response.data.settings;
    return {
      proactiveSuggestions: settings.proactive_suggestions,
      patternLearning: settings.pattern_learning,
      distractionAlerts: settings.distraction_alerts,
      autoActions: settings.auto_actions,
      updatedAt: settings.updated_at,
    };
  }

  async updateProactiveSettings(patch: Partial<ProactiveSettings>): Promise<void> {
    const payload: Record<string, boolean> = {};
    if (patch.proactiveSuggestions !== undefined) {
      payload.proactive_suggestions = patch.proactiveSuggestions;
    }
    if (patch.patternLearning !== undefined) {
      payload.pattern_learning = patch.patternLearning;
    }
    if (patch.distractionAlerts !== undefined) {
      payload.distraction_alerts = patch.distractionAlerts;
    }
    if (patch.autoActions !== undefined) {
      payload.auto_actions = patch.autoActions;
    }
    await api.put('/proactive-intelligence/settings', payload);
  }

  // Agent / Orchestrator
  async getAgentSuggestions(): Promise<unknown[]> {
    const response = await api.get<{ suggestions: unknown[] }>('/agent/suggestions');
    return response.data.suggestions;
  }

  async executeAgentSuggestion(suggestionId: string): Promise<void> {
    await api.post(`/agent/execute-suggestion/${suggestionId}`);
  }

  async proactiveManage(): Promise<void> {
    await api.post('/agent/proactive-manage');
  }

  async autoOrganize(): Promise<void> {
    await api.post('/agent/auto-organize');
  }

  async analyzePatterns(): Promise<void> {
    await api.post('/agent/analyze-patterns');
  }

  async smartPreload(): Promise<{ preloaded_tabs: unknown[]; preload_count: number; ready: boolean }> {
    const response = await api.post<{ preloaded_tabs: unknown[]; preload_count: number; ready: boolean }>(
      '/agent/smart-preload',
    );
    return response.data;
  }

  async getServiceStatus(): Promise<{ services: Record<string, string>; healthy: boolean }> {
    const response = await api.get<{ services: Record<string, string>; healthy: boolean }>(
      '/agent/service-status',
    );
    return response.data;
  }

  /** Inferred goals from proactive intelligence (behavior patterns). */
  async getInferredGoals(): Promise<
    Array<{ id: string; title: string; description: string; confidence: number; status: string }>
  > {
    const response = await api.get<{
      goals: Array<{ id: string; title: string; description: string; confidence: number; status: string }>;
    }>('/proactive-intelligence/goals');
    return response.data.goals ?? [];
  }

  async confirmInferredGoal(goalId: string): Promise<void> {
    await api.post(`/proactive-intelligence/goals/${goalId}/confirm`);
  }
}

export function mapSkillPredictionsToUi(data?: Record<string, unknown>): TabPrediction[] {
  const raw = data?.predictions;
  if (!Array.isArray(raw)) return [];
  return raw.map((p, idx) => mapAgentTabPredictionToUi(p as Record<string, unknown>, idx));
}

export const tabService = new TabService();